/** Монохромный набор иконок 24×24, штрих 1.5.
 *
 * Иконки наследуют цвет текста (`currentColor`) — тон задаётся окружением,
 * а не самой иконкой. Это держит интерфейс спокойным: цвет в нём означает
 * статус, а не «здесь нарисована картинка».
 *
 * Все иконки декоративные: смысл несёт соседний текст, поэтому aria-hidden.
 */
import type { ReactNode } from "react";

const shapes = {
  // --- навигация и каркас ---
  home: <><path d="M3 10.5 12 3l9 7.5" /><path d="M5 9.5V20a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9.5" /><path d="M9.5 21v-6h5v6" /></>,
  dashboard: <><rect x="3" y="3" width="7.5" height="9" rx="1.5" /><rect x="13.5" y="3" width="7.5" height="5.5" rx="1.5" /><rect x="13.5" y="12" width="7.5" height="9" rx="1.5" /><rect x="3" y="15.5" width="7.5" height="5.5" rx="1.5" /></>,
  processes: <><path d="M4 6h10" /><path d="M4 12h16" /><path d="M4 18h7" /><circle cx="17.5" cy="6" r="2.5" /><circle cx="14.5" cy="18" r="2.5" /></>,
  menu: <><path d="M3.5 6.5h17" /><path d="M3.5 12h17" /><path d="M3.5 17.5h17" /></>,
  close: <><path d="m6.5 6.5 11 11" /><path d="m17.5 6.5-11 11" /></>,
  chevronDown: <path d="m6 9.5 6 6 6-6" />,
  chevronRight: <path d="m9.5 6 6 6-6 6" />,
  chevronLeft: <path d="m14.5 6-6 6 6 6" />,
  chevronUpDown: <><path d="m8 9.5 4-4 4 4" /><path d="m16 14.5-4 4-4-4" /></>,
  arrowRight: <><path d="M4 12h15.5" /><path d="m13.5 6 6 6-6 6" /></>,
  arrowLeft: <><path d="M20 12H4.5" /><path d="m10.5 6-6 6 6 6" /></>,
  arrowUpRight: <><path d="M7 17 17 7" /><path d="M8.5 7H17v8.5" /></>,
  externalLink: <><path d="M14 4h6v6" /><path d="M20 4 11 13" /><path d="M18 14v5a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 4 19V8a1.5 1.5 0 0 1 1.5-1.5H10" /></>,
  more: <><circle cx="5" cy="12" r="1.4" /><circle cx="12" cy="12" r="1.4" /><circle cx="19" cy="12" r="1.4" /></>,

  // --- документы и данные ---
  document: <><path d="M13.5 3H6.5A1.5 1.5 0 0 0 5 4.5v15A1.5 1.5 0 0 0 6.5 21h11a1.5 1.5 0 0 0 1.5-1.5V8.5Z" /><path d="M13.5 3v4a1.5 1.5 0 0 0 1.5 1.5h4" /><path d="M8.5 13h7" /><path d="M8.5 17h4.5" /></>,
  clipboard: <><rect x="5" y="5" width="14" height="16" rx="2" /><path d="M9 5V4a1.5 1.5 0 0 1 1.5-1.5h3A1.5 1.5 0 0 1 15 4v1" /><path d="M9 12h6" /><path d="M9 16h4" /></>,
  folder: <><path d="M3.5 7.5A1.5 1.5 0 0 1 5 6h4l2 2.5h8a1.5 1.5 0 0 1 1.5 1.5v8A1.5 1.5 0 0 1 19 19.5H5A1.5 1.5 0 0 1 3.5 18Z" /></>,
  layers: <><path d="m12 3 8.5 4.5L12 12 3.5 7.5Z" /><path d="m3.5 12 8.5 4.5L20.5 12" /><path d="m3.5 16.5 8.5 4.5 8.5-4.5" /></>,
  chart: <><path d="M4 20V4" /><path d="M4 20h16" /><path d="M8 17v-5" /><path d="M13 17V8" /><path d="M18 17v-7" /></>,
  trend: <><path d="m3.5 15.5 5-5 3.5 3.5 6-6.5" /><path d="M14.5 7.5h4v4" /></>,
  compare: <><path d="M12 3.5v17" /><path d="M7 6.5 3.5 14h7Z" /><path d="M17 6.5 13.5 14h7Z" /><path d="M3.5 14a3.5 3.5 0 0 0 7 0" /><path d="M13.5 14a3.5 3.5 0 0 0 7 0" /></>,
  table: <><rect x="3.5" y="4.5" width="17" height="15" rx="2" /><path d="M3.5 9.5h17" /><path d="M9.5 9.5v10" /></>,

  // --- почта и общение ---
  mail: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3.5 7.5 7.6 5.3a1.5 1.5 0 0 0 1.8 0l7.6-5.3" /></>,
  inbox: <><path d="M3.5 13h4l1.5 3h6l1.5-3h4" /><path d="M6 4.5h12l3 8.5v5a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 18v-5Z" /></>,
  send: <><path d="M20.5 3.5 3.5 10l7 3 3 7Z" /><path d="m10.5 13 4-4" /></>,
  message: <><path d="M20.5 4.5h-17v11h4v4l5-4h8Z" /></>,
  bell: <><path d="M18 9a6 6 0 1 0-12 0c0 5-2 6-2 6h16s-2-1-2-6" /><path d="M13.7 19a2 2 0 0 1-3.4 0" /></>,
  sparkles: <><path d="m12 3.5 1.9 5 5 1.9-5 1.9-1.9 5-1.9-5-5-1.9 5-1.9Z" /><path d="M19 3v3" /><path d="M17.5 4.5h3" /><path d="M5.5 16v2.5" /><path d="M4.25 17.25h2.5" /></>,

  // --- закупки ---
  building: <><path d="M4 21V5.5L11 3v18" /><path d="M11 10h8a1 1 0 0 1 1 1v10" /><path d="M2.5 21h19" /><path d="M7 8h1" /><path d="M7 12h1" /><path d="M7 16h1" /><path d="M15 14h1.5" /><path d="M15 17.5h1.5" /></>,
  cart: <><path d="M2.5 3.5h2.2l2.3 11h11" /><path d="M6.6 7h14.4l-1.7 6.5H7.9" /><circle cx="9" cy="19.5" r="1.4" /><circle cx="17.5" cy="19.5" r="1.4" /></>,
  box: <><path d="m12 3 8.5 4.5v9L12 21l-8.5-4.5v-9Z" /><path d="m3.5 7.5 8.5 4.6 8.5-4.6" /><path d="M12 12.1V21" /></>,
  truck: <><path d="M3 6.5h10.5v10H3Z" /><path d="M13.5 10h3.8l3.2 3.8v2.7h-7" /><circle cx="7" cy="18.5" r="1.8" /><circle cx="17" cy="18.5" r="1.8" /></>,
  wallet: <><path d="M3.5 7.5A1.5 1.5 0 0 1 5 6h12.5a1.5 1.5 0 0 1 1.5 1.5V9" /><path d="M3.5 7.5V18A1.5 1.5 0 0 0 5 19.5h14A1.5 1.5 0 0 0 20.5 18v-7.5A1.5 1.5 0 0 0 19 9H5" /><circle cx="16.5" cy="14" r="1.2" /></>,
  receipt: <><path d="M5.5 3.5v17l2.5-1.6 2.5 1.6 2.5-1.6 2.5 1.6 2.5-1.6V3.5l-2.5 1.6L13 3.5l-2.5 1.6L8 3.5Z" /><path d="M9 9.5h6" /><path d="M9 13.5h4" /></>,
  tag: <><path d="M3.5 11.6V4.5a1 1 0 0 1 1-1h7.1a1 1 0 0 1 .7.3l8 8a1 1 0 0 1 0 1.4l-7.1 7.1a1 1 0 0 1-1.4 0l-8-8a1 1 0 0 1-.3-.7Z" /><circle cx="8" cy="8" r="1.3" /></>,
  ruler: <><path d="m15.5 2.5 6 6L8.5 21.5l-6-6Z" /><path d="m12.5 5.5 2.5 2.5" /><path d="m9.5 8.5 2 2" /><path d="m6.5 11.5 2.5 2.5" /></>,
  flag: <><path d="M5.5 21V4" /><path d="M5.5 4.5c4.5-3 9 3 13.5 0v9c-4.5 3-9-3-13.5 0Z" /></>,
  route: <><circle cx="6" cy="5.5" r="2.5" /><circle cx="18" cy="18.5" r="2.5" /><path d="M8.5 5.5h6a3.5 3.5 0 0 1 0 7h-5a3 3 0 0 0 0 6h6" /></>,
  briefcase: <><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M8.5 7V5.5A1.5 1.5 0 0 1 10 4h4a1.5 1.5 0 0 1 1.5 1.5V7" /><path d="M3 12.5c3 1.3 6 2 9 2s6-.7 9-2" /></>,

  // --- люди ---
  user: <><circle cx="12" cy="8" r="3.8" /><path d="M4.5 20.5a7.5 7.5 0 0 1 15 0" /></>,
  users: <><circle cx="9.5" cy="8" r="3.5" /><path d="M3 20.5a6.5 6.5 0 0 1 13 0" /><path d="M16 4.8a3.5 3.5 0 0 1 0 6.4" /><path d="M17.5 14.5a6.5 6.5 0 0 1 3.5 6" /></>,
  logout: <><path d="M10 4.5H5.5A1.5 1.5 0 0 0 4 6v12a1.5 1.5 0 0 0 1.5 1.5H10" /><path d="M9.5 12h11" /><path d="m16.5 8 4 4-4 4" /></>,

  // --- статусы и действия ---
  check: <path d="m5 12.5 4.5 4.5L19 7" />,
  checkCircle: <><circle cx="12" cy="12" r="8.5" /><path d="m8.5 12 2.5 2.5 4.5-5" /></>,
  approval: <><path d="M12 3 4.5 6.3v5c0 4.6 3.2 8 7.5 9.7 4.3-1.7 7.5-5.1 7.5-9.7v-5Z" /><path d="m9 12 2.2 2.2 4.3-4.7" /></>,
  alert: <><path d="M12 3.8 21 20H3Z" /><path d="M12 10v4.5" /><path d="M12 17.2h.01" /></>,
  info: <><circle cx="12" cy="12" r="8.5" /><path d="M12 11v5.5" /><path d="M12 7.8h.01" /></>,
  clock: <><circle cx="12" cy="12" r="8.5" /><path d="M12 7v5.2l3.2 1.9" /></>,
  history: <><path d="M3.5 12a8.5 8.5 0 1 0 2.6-6.1" /><path d="M3.5 4v4.5H8" /><path d="M12 8v4.3l3 1.7" /></>,
  pause: <><rect x="8" y="5" width="3" height="14" rx="1" /><rect x="14" y="5" width="3" height="14" rx="1" /></>,
  play: <path d="M7.5 5.2v13.6L19 12Z" />,
  refresh: <><path d="M20 12a8 8 0 1 1-2.4-5.7" /><path d="M20.5 4v4.5H16" /></>,
  ban: <><circle cx="12" cy="12" r="8.5" /><path d="m6.2 6.2 11.6 11.6" /></>,

  // --- работа с данными ---
  search: <><circle cx="10.5" cy="10.5" r="6.5" /><path d="m15.5 15.5 5 5" /></>,
  filter: <><path d="M3.5 5.5h17l-6.5 7.6V20l-4 1.5v-8.4Z" /></>,
  plus: <><path d="M12 5v14" /><path d="M5 12h14" /></>,
  minus: <path d="M5 12h14" />,
  edit: <><path d="M4 20.2 4.8 16 16.6 4.3a1.8 1.8 0 0 1 2.6 0l.5.5a1.8 1.8 0 0 1 0 2.6L8 19.3Z" /><path d="m15.5 5.5 3 3" /></>,
  trash: <><path d="M4 6.5h16" /><path d="M9.5 6.5v-2A1 1 0 0 1 10.5 3.5h3a1 1 0 0 1 1 1v2" /><path d="m6 6.5.9 13a1.5 1.5 0 0 0 1.5 1.4h7.2a1.5 1.5 0 0 0 1.5-1.4l.9-13" /><path d="M10.5 10.5v6.5" /><path d="M13.5 10.5v6.5" /></>,
  copy: <><rect x="8.5" y="8.5" width="12" height="12" rx="2" /><path d="M15.5 8.5v-3a2 2 0 0 0-2-2h-8a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h3" /></>,
  attachment: <path d="M20 11.5 12 19.5a5 5 0 0 1-7-7l8.2-8.2a3.4 3.4 0 0 1 4.8 4.8L9.7 17.3a1.8 1.8 0 0 1-2.5-2.5l7.6-7.6" />,
  upload: <><path d="M12 16V4" /><path d="m7.5 8.5 4.5-4.5 4.5 4.5" /><path d="M4 15.5V19a1.5 1.5 0 0 0 1.5 1.5h13A1.5 1.5 0 0 0 20 19v-3.5" /></>,
  download: <><path d="M12 4v12" /><path d="m7.5 11.5 4.5 4.5 4.5-4.5" /><path d="M4 16.5V19a1.5 1.5 0 0 0 1.5 1.5h13A1.5 1.5 0 0 0 20 19v-2.5" /></>,
  image: <><rect x="3.5" y="4.5" width="17" height="15" rx="2" /><circle cx="8.5" cy="9.5" r="1.5" /><path d="m4 17 4.5-4.5 3 3 4-4.5 5 6" /></>,
  link: <><path d="M10.5 13.5a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1.6 1.6" /><path d="M13.5 10.5a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1.6-1.6" /></>,

  // --- настройки ---
  settings: <><circle cx="12" cy="12" r="3" /><path d="M19.2 14.5a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-1.8-.3 1.6 1.6 0 0 0-1 1.5V21a2 2 0 0 1-4 0v-.2a1.6 1.6 0 0 0-1-1.4 1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0 .3-1.8 1.6 1.6 0 0 0-1.5-1H3a2 2 0 0 1 0-4h.2a1.6 1.6 0 0 0 1.4-1 1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H9a1.6 1.6 0 0 0 1-1.5V3a2 2 0 0 1 4 0v.2a1.6 1.6 0 0 0 1 1.4 1.6 1.6 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V9a1.6 1.6 0 0 0 1.5 1H21a2 2 0 0 1 0 4h-.2a1.6 1.6 0 0 0-1.4 1Z" /></>,
  sliders: <><path d="M4 6.5h9" /><path d="M17 6.5h3" /><path d="M4 17.5h4" /><path d="M12 17.5h8" /><circle cx="15" cy="6.5" r="2.2" /><circle cx="10" cy="17.5" r="2.2" /></>,
  lock: <><rect x="4.5" y="10" width="15" height="10.5" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></>,
  key: <><circle cx="8" cy="15" r="4" /><path d="m11 12 8.5-8.5" /><path d="m16.5 6.5 2 2" /><path d="m14 9 2.5 2.5" /></>,
  shield: <><path d="M12 3 4.5 6.3v5c0 4.6 3.2 8 7.5 9.7 4.3-1.7 7.5-5.1 7.5-9.7v-5Z" /></>,
  sun: <><circle cx="12" cy="12" r="4" /><path d="M12 2.5v2" /><path d="M12 19.5v2" /><path d="M2.5 12h2" /><path d="M19.5 12h2" /><path d="m5.3 5.3 1.4 1.4" /><path d="m17.3 17.3 1.4 1.4" /><path d="m18.7 5.3-1.4 1.4" /><path d="m6.7 17.3-1.4 1.4" /></>,
  moon: <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4 8.5 8.5 0 1 0 20 14.5Z" />,
  monitor: <><rect x="3" y="4" width="18" height="12.5" rx="2" /><path d="M8.5 20.5h7" /><path d="M12 16.5v4" /></>,

  circle: <circle cx="12" cy="12" r="7.5" />,
  dot: <circle cx="12" cy="12" r="3.5" fill="currentColor" stroke="none" />,
} as const;

export type IconName = keyof typeof shapes;

/** Совместимость: раньше тон задавал иконке собственный цвет. Теперь тон —
 * это класс текста, и иконка просто наследует его. */
export type IconTone =
  | "indigo" | "teal" | "blue" | "emerald" | "slate" | "amber" | "rose"
  | "green" | "red" | "muted" | "accent" | "auto";

const toneClass: Record<Exclude<IconTone, "auto">, string> = {
  indigo: "text-accent",
  accent: "text-accent",
  teal: "text-success",
  emerald: "text-success",
  green: "text-success",
  blue: "text-info",
  amber: "text-warning",
  rose: "text-danger",
  red: "text-danger",
  slate: "text-muted",
  muted: "text-muted",
};

interface IconProps {
  name: IconName;
  size?: number;
  className?: string;
  /** Не указывайте без нужды: по умолчанию иконка берёт цвет текста рядом. */
  tone?: IconTone;
  /** Толщина штриха; 2 — для крупных размеров, 1.25 — для мелких. */
  strokeWidth?: number;
}

export default function Icon({
  name,
  size = 16,
  className = "",
  tone,
  strokeWidth = 1.5,
}: IconProps) {
  const colour = tone && tone !== "auto" ? toneClass[tone] : "";
  return (
    <svg
      data-icon={name}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={`inline-block shrink-0 align-[-0.125em] ${colour} ${className}`}
    >
      {shapes[name] as ReactNode}
    </svg>
  );
}

/* --------------------------------------------------------------------------
   Совместимость со словарями представления, где шаги и статусы всё ещё
   описаны эмодзи. Эмодзи в интерфейсе не рисуем — подменяем иконкой.
   -------------------------------------------------------------------------- */

const glyphs: Record<string, IconName> = {
  "🏠": "home", "📝": "document", "⚙": "sliders", "📣": "send", "✅": "approval",
  "✓": "check", "✔": "check", "📧": "mail", "✉": "mail", "📨": "inbox", "📥": "inbox",
  "🏭": "building", "🛒": "cart", "🤖": "sparkles", "🧠": "sparkles", "📋": "clipboard",
  "📒": "clipboard", "🛠": "settings", "🔧": "settings", "📦": "box", "📏": "ruler",
  "🛣": "route", "👥": "users", "🏢": "building", "👤": "user", "🔍": "search",
  "🔎": "search", "⏰": "clock", "⏳": "clock", "⚠": "alert", "ℹ": "info",
  "💰": "wallet", "💳": "receipt", "🧾": "receipt", "⚖": "compare", "🏁": "flag",
  "🎉": "checkCircle", "➕": "plus", "✎": "edit", "📎": "attachment", "📤": "upload",
  "🗑": "trash", "💬": "message", "📜": "document", "💼": "briefcase", "⏭": "arrowRight",
  "❓": "info", "✕": "close", "↩": "arrowLeft", "🔒": "lock", "🖼": "image",
  "📕": "document", "📘": "document", "📗": "document", "🗜": "attachment",
  "📄": "document", "🌐": "send", "🗂": "folder", "⚡": "processes", "📊": "chart",
  "📈": "trend", "📉": "trend", "🔔": "bell", "🚚": "truck", "🏷": "tag",
};

export function GlyphIcon({ glyph, ...props }: { glyph: string } & Omit<IconProps, "name">) {
  return <Icon name={glyphs[glyph.replace(/️/g, "")] || "circle"} {...props} />;
}

/** Для служебных подписей действий: ведущее эмодзи заменяется иконкой. */
export function IconLabel({ text }: { text: string }) {
  const match = text.match(/^(\p{Extended_Pictographic}|[✓✔✎✕↩])️?\s*/u);
  if (!match) return <>{text}</>;
  return (
    <>
      <GlyphIcon glyph={match[0].trim()} size={15} className="mr-1.5" />
      <span>{text.slice(match[0].length)}</span>
    </>
  );
}
